// AdminTenantsPage — Phase 16 Gate 3 — superadmin tenant lifecycle.

import { useEffect, useMemo, useState } from "react";
import { Check, Copy, Mail, RefreshCw, X } from "lucide-react";
import { admin } from "@/api/endpoints";
import { serverDetail } from "@/api/client";

function CapsuleLinks({ req, subdomain }) {
    const partyId = req.capsule_party_id;
    const oppId = req.capsule_opportunity_id;
    const synced = req.capsule_synced_at;
    const err = req.capsule_sync_error;

    if (!synced && !partyId && !err) {
        return (
            <span style={{ color: "var(--ux-text-3)", fontSize: 12 }}
                  data-testid={`capsule-pending-${req.id}`}>
                pending sync
            </span>
        );
    }
    if (err && !partyId) {
        return (
            <span style={{ color: "var(--ux-warning)", fontSize: 12 }}
                  title={err}
                  data-testid={`capsule-failed-${req.id}`}>
                Sync failed
            </span>
        );
    }
    const partyHref = subdomain && partyId ? `https://${subdomain}.capsulecrm.com/party/${partyId}` : null;
    const oppHref = subdomain && oppId ? `https://${subdomain}.capsulecrm.com/opportunity/${oppId}` : null;
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 2, fontSize: 12 }}>
            {partyId ? (
                partyHref ? (
                    <a href={partyHref} target="_blank" rel="noreferrer"
                       style={{ color: "var(--ux-accent)" }}
                       data-testid={`capsule-party-link-${req.id}`}>
                        Contact #{partyId}
                    </a>
                ) : (
                    <span style={{ color: "var(--ux-text-2)", fontFamily: "JetBrains Mono, monospace" }}
                          data-testid={`capsule-party-id-${req.id}`}>
                        Contact #{partyId}
                    </span>
                )
            ) : (
                <span style={{ color: "var(--ux-text-3)" }}>—</span>
            )}
            {oppId ? (
                oppHref ? (
                    <a href={oppHref} target="_blank" rel="noreferrer"
                       style={{ color: "var(--ux-accent)" }}
                       data-testid={`capsule-opp-link-${req.id}`}>
                        Opportunity #{oppId}
                    </a>
                ) : (
                    <span style={{ color: "var(--ux-text-2)", fontFamily: "JetBrains Mono, monospace" }}
                          data-testid={`capsule-opp-id-${req.id}`}>
                        Opportunity #{oppId}
                    </span>
                )
            ) : (
                <span style={{ color: "var(--ux-text-3)" }}>—</span>
            )}
        </div>
    );
}

function StatusPill({ status }) {
    const palette = {
        pending: { fg: "#f5b544", bg: "#3a2a0f" },
        approved: { fg: "#3da9fc", bg: "#1e3a52" },
        rejected: { fg: "#f25f5c", bg: "#3a1513" },
        consumed: { fg: "#3ccb7f", bg: "#163a28" },
        active: { fg: "#3ccb7f", bg: "#163a28" },
        expired: { fg: "#a7b1c2", bg: "#1c2431" },
        suspended: { fg: "#f25f5c", bg: "#3a1513" },
    };
    const c = palette[status] || palette.expired;
    return (
        <span style={{
            display: "inline-block",
            padding: "3px 10px",
            borderRadius: 999,
            fontSize: 11,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: c.fg,
            background: c.bg,
            fontFamily: "JetBrains Mono, monospace",
        }} data-testid={`tenant-status-${status}`}>
            {status}
        </span>
    );
}

export default function AdminTenantsPage() {
    const [accessReqs, setAccessReqs] = useState([]);
    const [tenants, setTenants] = useState([]);
    const [statusFilter, setStatusFilter] = useState("pending");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [inviteResult, setInviteResult] = useState(null);
    const [rejectingId, setRejectingId] = useState(null);
    const [rejectReason, setRejectReason] = useState("");
    const [capsuleSubdomain, setCapsuleSubdomain] = useState("");
    const [capsuleConfigured, setCapsuleConfigured] = useState(false);

    useEffect(() => {
        admin.capsuleConfig()
            .then((cfg) => {
                setCapsuleSubdomain(cfg?.subdomain || "");
                setCapsuleConfigured(Boolean(cfg?.configured));
            })
            .catch(() => {});
    }, []);

    async function refresh() {
        setBusy(true);
        setErr(null);
        try {
            const [reqs, ts] = await Promise.all([
                admin.listAccessRequests(statusFilter || null),
                admin.listTenants(),
            ]);
            setAccessReqs(Array.isArray(reqs) ? reqs : []);
            setTenants(ts.tenants || []);
        } catch (e) {
            setErr(serverDetail(e));
        } finally {
            setBusy(false);
        }
    }

    // Phase 14.8 W5 — `refresh` is recreated on every render (declared
    // as `async function`, not `useCallback`); adding it to deps would
    // refire the API call infinitely. Effect intentionally fires on
    // statusFilter change only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { refresh(); }, [statusFilter]);

    async function approve(req) {
        setBusy(true);
        try {
            const res = await admin.approveAccessRequest(req.id);
            setInviteResult(res);
            await refresh();
        } catch (e) {
            setErr(serverDetail(e));
        } finally {
            setBusy(false);
        }
    }

    async function reject(req) {
        if (!rejectReason.trim()) return;
        setBusy(true);
        try {
            await admin.rejectAccessRequest(req.id, rejectReason.trim());
            setRejectingId(null);
            setRejectReason("");
            await refresh();
        } catch (e) {
            setErr(serverDetail(e));
        } finally {
            setBusy(false);
        }
    }

    async function suspend(t) {
        if (!t.subscription) return;
        try {
            await admin.updateSubscription(t.tenant_id, { status: "suspended" });
            await refresh();
        } catch (e) { setErr(serverDetail(e)); }
    }

    async function reactivate(t) {
        try {
            await admin.grantSubscription(t.tenant_id, 365, "Reactivated via Tenants console");
            await refresh();
        } catch (e) { setErr(serverDetail(e)); }
    }

    function copyInvite() {
        if (!inviteResult) return;
        navigator.clipboard?.writeText(inviteResult.invite_url).catch(() => {});
    }

    const pendingCount = useMemo(
        () => accessReqs.filter((r) => r.status === "pending").length,
        [accessReqs],
    );

    return (
        <div className="space-y-6" data-testid="admin-tenants-page" style={{ padding: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                    <h1 style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 24, color: "var(--ux-text-1)" }}>
                        Tenants & Access Requests
                    </h1>
                    <p style={{ color: "var(--ux-text-3)", fontSize: 13 }}>
                        Approve or reject incoming access requests and manage active subscriptions.
                    </p>
                </div>
                <button
                    type="button"
                    className="btn-secondary"
                    onClick={refresh}
                    disabled={busy}
                    data-testid="admin-tenants-refresh"
                >
                    <RefreshCw size={14} /> Refresh
                </button>
            </div>

            {err && <div className="banner banner--danger" data-testid="admin-tenants-error">{err}</div>}

            {inviteResult && (
                <div className="banner banner--success" data-testid="admin-tenants-invite-result">
                    <div style={{ marginBottom: 8 }}>
                        Invite issued for <strong>{inviteResult.user_email}</strong>{" "}
                        ({inviteResult.organisation}).
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        <code style={{
                            background: "var(--ux-bg-0)",
                            padding: "8px 12px",
                            borderRadius: 6,
                            fontSize: 12,
                            wordBreak: "break-all",
                            flex: "1 1 280px",
                            color: "var(--ux-text-1)",
                        }}>
                            {inviteResult.invite_url}
                        </code>
                        <button type="button" className="btn-secondary" onClick={copyInvite} data-testid="admin-tenants-copy-invite">
                            <Copy size={14} /> Copy link
                        </button>
                        <button type="button" className="btn-secondary" onClick={() => setInviteResult(null)} data-testid="admin-tenants-dismiss-invite">
                            Dismiss
                        </button>
                    </div>
                    <div style={{ marginTop: 6, fontSize: 12, color: "var(--ux-text-3)" }}>
                        Email this link to the requester. Token expires in 7 days.
                    </div>
                </div>
            )}

            {/* Access requests */}
            <section data-testid="admin-tenants-access-requests">
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
                    <h2 style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 18, color: "var(--ux-text-1)" }}>
                        Access requests
                    </h2>
                    <span style={{ color: "var(--ux-text-3)", fontSize: 12 }}>
                        ({accessReqs.length}, {pendingCount} pending)
                    </span>
                    {capsuleConfigured && (
                        <span
                            data-testid="admin-tenants-capsule-status"
                            style={{
                                fontSize: 11,
                                color: "var(--ux-success)",
                                background: "var(--ux-success-soft)",
                                border: "1px solid var(--ux-success)",
                                padding: "2px 8px",
                                borderRadius: 999,
                                letterSpacing: "0.04em",
                                textTransform: "uppercase",
                            }}
                            title={`Capsule subdomain: ${capsuleSubdomain || "(not configured — IDs shown without links)"}`}
                        >
                            Capsule sync ON
                        </span>
                    )}
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        data-testid="admin-tenants-status-filter"
                        style={{
                            marginLeft: "auto",
                            background: "var(--ux-bg-0)",
                            color: "var(--ux-text-1)",
                            border: "1px solid var(--ux-border-2)",
                            padding: "6px 10px",
                            borderRadius: 6,
                        }}
                    >
                        <option value="">All</option>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                        <option value="consumed">Consumed</option>
                    </select>
                </div>
                <div style={{ overflowX: "auto", border: "1px solid var(--ux-border-1)", borderRadius: 8 }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", color: "var(--ux-text-1)", fontSize: 13 }}>
                        <thead>
                            <tr style={{ background: "var(--ux-bg-1)", textAlign: "left" }}>
                                <th style={{ padding: "10px 12px" }}>Submitted</th>
                                <th style={{ padding: "10px 12px" }}>Status</th>
                                <th style={{ padding: "10px 12px" }}>Email</th>
                                <th style={{ padding: "10px 12px" }}>Organisation</th>
                                <th style={{ padding: "10px 12px" }}>Country</th>
                                <th style={{ padding: "10px 12px" }}>Use case</th>
                                <th style={{ padding: "10px 12px" }}>Capsule CRM</th>
                                <th style={{ padding: "10px 12px", textAlign: "right" }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {accessReqs.length === 0 ? (
                                <tr><td colSpan={8} style={{ padding: 24, textAlign: "center", color: "var(--ux-text-3)" }}>
                                    No access requests in this filter.
                                </td></tr>
                            ) : accessReqs.map((req) => (
                                <tr key={req.id} style={{ borderTop: "1px solid var(--ux-border-1)" }}
                                    data-testid={`admin-tenants-row-${req.id}`}>
                                    <td style={{ padding: "10px 12px", color: "var(--ux-text-2)", fontSize: 12 }}>
                                        {new Date(req.submitted_at).toLocaleString()}
                                    </td>
                                    <td style={{ padding: "10px 12px" }}><StatusPill status={req.status} /></td>
                                    <td style={{ padding: "10px 12px" }}>{req.email}</td>
                                    <td style={{ padding: "10px 12px" }}>{req.organisation}</td>
                                    <td style={{ padding: "10px 12px" }}>{req.country}</td>
                                    <td style={{ padding: "10px 12px", maxWidth: 320, color: "var(--ux-text-2)" }}>
                                        <span style={{ display: "block", maxHeight: 64, overflow: "hidden", textOverflow: "ellipsis" }}>
                                            {req.use_case}
                                        </span>
                                    </td>
                                    <td style={{ padding: "10px 12px" }}>
                                        <CapsuleLinks req={req} subdomain={capsuleSubdomain} />
                                    </td>
                                    <td style={{ padding: "10px 12px", textAlign: "right" }}>
                                        {req.status === "pending" && rejectingId === req.id ? (
                                            <div style={{ display: "flex", gap: 6, alignItems: "center", justifyContent: "flex-end" }}>
                                                <input
                                                    type="text"
                                                    value={rejectReason}
                                                    onChange={(e) => setRejectReason(e.target.value)}
                                                    placeholder="Reason"
                                                    data-testid={`admin-tenants-reject-reason-${req.id}`}
                                                    style={{
                                                        background: "var(--ux-bg-0)", color: "var(--ux-text-1)",
                                                        border: "1px solid var(--ux-border-2)",
                                                        padding: "4px 8px", borderRadius: 4, fontSize: 12,
                                                    }}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn-secondary"
                                                    onClick={() => reject(req)}
                                                    data-testid={`admin-tenants-reject-confirm-${req.id}`}
                                                >Confirm</button>
                                                <button
                                                    type="button"
                                                    className="btn-secondary"
                                                    onClick={() => { setRejectingId(null); setRejectReason(""); }}
                                                    data-testid={`admin-tenants-reject-cancel-${req.id}`}
                                                >Cancel</button>
                                            </div>
                                        ) : req.status === "pending" ? (
                                            <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                                                <button
                                                    type="button"
                                                    className="btn-primary"
                                                    onClick={() => approve(req)}
                                                    disabled={busy}
                                                    data-testid={`admin-tenants-approve-${req.id}`}
                                                ><Check size={14} /> Approve</button>
                                                <button
                                                    type="button"
                                                    className="btn-secondary"
                                                    onClick={() => { setRejectingId(req.id); setRejectReason(""); }}
                                                    data-testid={`admin-tenants-reject-${req.id}`}
                                                ><X size={14} /> Reject</button>
                                            </div>
                                        ) : (
                                            <span style={{ color: "var(--ux-text-3)", fontSize: 12 }}>—</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Active tenants */}
            <section data-testid="admin-tenants-list">
                <h2 style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 18, color: "var(--ux-text-1)", marginBottom: 12 }}>
                    Tenants ({tenants.length})
                </h2>
                <div style={{ overflowX: "auto", border: "1px solid var(--ux-border-1)", borderRadius: 8 }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", color: "var(--ux-text-1)", fontSize: 13 }}>
                        <thead>
                            <tr style={{ background: "var(--ux-bg-1)", textAlign: "left" }}>
                                <th style={{ padding: "10px 12px" }}>Username</th>
                                <th style={{ padding: "10px 12px" }}>Role</th>
                                <th style={{ padding: "10px 12px" }}>Email</th>
                                <th style={{ padding: "10px 12px" }}>Organisation</th>
                                <th style={{ padding: "10px 12px" }}>Subscription</th>
                                <th style={{ padding: "10px 12px" }}>Expires</th>
                                <th style={{ padding: "10px 12px", textAlign: "right" }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tenants.length === 0 ? (
                                <tr><td colSpan={7} style={{ padding: 24, textAlign: "center", color: "var(--ux-text-3)" }}>
                                    No tenants yet.
                                </td></tr>
                            ) : tenants.map((t) => (
                                <tr key={t.tenant_id} style={{ borderTop: "1px solid var(--ux-border-1)" }}
                                    data-testid={`admin-tenants-tenant-row-${t.tenant_id}`}>
                                    <td style={{ padding: "10px 12px", fontFamily: "JetBrains Mono, monospace" }}>{t.user.username}</td>
                                    <td style={{ padding: "10px 12px" }}>{t.user.role}</td>
                                    <td style={{ padding: "10px 12px", color: "var(--ux-text-2)" }}>{t.user.email || "—"}</td>
                                    <td style={{ padding: "10px 12px", color: "var(--ux-text-2)" }}>{t.user.organisation || "—"}</td>
                                    <td style={{ padding: "10px 12px" }}>
                                        {t.subscription ? <StatusPill status={t.subscription.status} /> : <span style={{ color: "var(--ux-text-3)" }}>none</span>}
                                    </td>
                                    <td style={{ padding: "10px 12px", color: "var(--ux-text-2)", fontSize: 12 }}>
                                        {t.subscription?.expires_at ? new Date(t.subscription.expires_at).toLocaleDateString() : "—"}
                                    </td>
                                    <td style={{ padding: "10px 12px", textAlign: "right" }}>
                                        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                                            {t.user.role !== "superadmin" && t.subscription && t.subscription.status === "active" && (
                                                <button
                                                    type="button"
                                                    className="btn-secondary"
                                                    onClick={() => suspend(t)}
                                                    data-testid={`admin-tenants-suspend-${t.tenant_id}`}
                                                >Suspend</button>
                                            )}
                                            {t.user.role !== "superadmin" && (!t.subscription || t.subscription.status !== "active") && (
                                                <button
                                                    type="button"
                                                    className="btn-primary"
                                                    onClick={() => reactivate(t)}
                                                    data-testid={`admin-tenants-reactivate-${t.tenant_id}`}
                                                >Reactivate</button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
}
